// NextScene - Google OAuth helpers (Emergent-managed)
//
// We use two flavours:
//  * `openGoogleRedirect()` (recommended) — full-page redirect via
//    window.location.assign, works on mobile + inside embedded webviews.
//    Adds `prompt=select_account` so users always see the account chooser
//    (fixes the "auto-login into wrong account" bug reported by users).
//  * `openGooglePopup()` (legacy) — kept only for callers that still
//    depend on the popup+postMessage flow.
import api from "./api";

const AUTH_URL = "https://auth.emergentagent.com/";

/**
 * Full-page Google sign-in via Emergent OAuth. Redirects the browser to
 * auth.emergentagent.com; on success the user comes back to the site with
 * `#session_id=<opaque>` in the URL — AuthCallback picks that up and
 * exchanges it for a session cookie via POST /auth/google/session.
 */
export function openGoogleRedirect({ redirectTo = "/app" } = {}) {
  const returnUrl = `${window.location.origin}${redirectTo}`;
  const params = new URLSearchParams({
    redirect: returnUrl,
    prompt: "select_account", // always show account chooser — fixes stuck auto-login
  });
  window.location.assign(`${AUTH_URL}?${params.toString()}`);
}

/**
 * Legacy popup flow. Prefer `openGoogleRedirect()` for new UI. Popup can be
 * blocked on mobile Safari and inside embedded webviews.
 */
export async function openGooglePopup() {
  const redirect = `${window.location.origin}/auth-popup`;
  const params = new URLSearchParams({ redirect, prompt: "select_account" });
  const url = `${AUTH_URL}?${params.toString()}`;
  const w = 500, h = 700;
  const left = Math.max(0, (window.screen.width - w) / 2);
  const top = Math.max(0, (window.screen.height - h) / 2);
  const popup = window.open(url, "nextscene-google", `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`);

  if (!popup) {
    // Popup blocked — fall back to same-tab redirect (with account chooser)
    openGoogleRedirect({ redirectTo: "/app" });
    return null;
  }

  return await new Promise((resolve, reject) => {
    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMsg);
      clearInterval(watcher);
    };
    const onMsg = async (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== "ns-google-session" || !e.data.session_id) return;
      cleanup();
      try { popup.close(); } catch {}
      try {
        const { data } = await api.post("/auth/google/session", null, {
          headers: { "X-Session-ID": e.data.session_id },
        });
        resolve(data);
      } catch (err) { reject(err); }
    };
    const watcher = setInterval(() => {
      if (popup.closed) { cleanup(); reject(new Error("Sign-in cancelled")); }
    }, 500);
    window.addEventListener("message", onMsg);
  });
}
